<template>
    <form ref="form" @submit.prevent>
        <DsInputField
            ref="nameInput"
            v-model="tag.name"
            data-qa="tag-name-input"
            type="text"
            name="title"
            :label="$t('tags.tagName')"
            required
            autofocus
            :submitted="submitted"
        />

        <DsMultiselect
            ref="categorySelect"
            v-model="tag.category"
            class="category-multiselect"
            data-qa="tag-category-multiselect"
            searchable
            allow-add
            :placeholder="$t('tags.tagCategory')"
            :options="tagCategories"
            @add="addNewCategory"
        />

        <DsTextAreaField
            v-model="tag.description"
            type="text"
            name="long"
            data-qa="tag-desc-input"
            :label="$t('tags.tagDescription')"
            :submitted="submitted"
        />

        <portal to="tagModalHeader">
            <DsFilledButton
                data-qa="create-tag-button"
                type="button"
                :loading="saving"
                :disabled="saveDisabled"
                @click="save"
            >
                {{ $t('global.save') }}
            </DsFilledButton>
        </portal>
    </form>
</template>

<script>
import { createTag } from '@/api/tags';

export default {
    props: {
        tagName: String,
        tags: Array,
        tagCategories: Array,
        newCategory: Object,
    },

    emits: ['tagAdded', 'addNewCategory'],

    data() {
        return {
            tag: {
                name: '',
                description: '',
                category: null,
            },
            submitted: false,
            saving: false,
        };
    },

    async mounted() {
        if (this.tagName) {
            this.tag.name = this.tagName;
        }

        await this.$nextTick();

        this.$refs.nameInput?.input_focus();
    },

    watch: {
        newCategory(value) {
            if (value) this.onNewTagCategoryAdded(value);
        },
    },

    computed: {
        saveDisabled() {
            return !this.tag.name.trim().length;
        },
    },

    methods: {
        async save() {
            if (this.saveDisabled) return;

            this.submitted = true;
            this.saving = true;

            const existingTag = this.findDuplicate();

            if (existingTag) {
                this.$emit('tagCreated', existingTag);
            } else if (this.$refs.form?.checkValidity()) {
                try {
                    const { name, description, category } = this.tag;

                    const tagId = await createTag({
                        name,
                        description,
                        category: category?.label || '',
                    });

                    this.$toast({ message: this.$t('tags.createTagSuccess') });
                    this.$emit('tagCreated', {
                        ...this.tag,
                        id: tagId,
                    });
                } catch (e) {
                    this.$error({ message: this.$t('tags.createTagError') });
                }
            }

            this.saving = false;
        },

        findDuplicate() {
            return this.tags.find((tag) => {
                const sameName = tag.name === this.tag.name;
                const sameCategory = (tag.category && this.tag.category)
                    ? tag.category.id === this.tag.category.value
                    : !(tag.category || this.tag.category);

                return sameName && sameCategory;
            });
        },

        addNewCategory() {
            this.$refs.categorySelect.toggle();

            this.$emit('addNewCategory');
        },

        onNewTagCategoryAdded({ id }) {
            this.tag.category = this.tagCategories.find(({ value }) => {
                return id === value;
            });
        },
    },
};
</script>

<style lang="scss" scoped>
.category-multiselect {
    --input-padding-top: #{$spacing-050};
}
</style>
